import { useCallback, useEffect, useMemo, useRef } from 'react'

import {
  BillOfMaterials,
  MAIN_DOCUMENT_CONTAINER_TOP_PADDING,
  ToolsBillOfMaterials,
  useAppStore,
  useDocumentState,
} from '@/state'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { usePDFifyStore } from '@/components/pdf/state'

import {
  DocumentContainer,
  InnerDocumentContainer,
  VirtualDocumentContainer,
} from '../../DocumentContainer'
import { OperationDocument } from '../../OperationDocument'
import { TitleDocument } from '../../TitleDocument'
import {
  BillOfMaterialsDocument,
  MAX_PARTS_PER_PAGE,
} from '../../BillOfMaterialsDocument'
import { ToolsBillOfMaterialsDocument } from '../../ToolsBillOfMaterialsDocument'
import { VersionHistoryDocument } from '../../VersionHistory'
import { useListPageViews } from '@/services/queries/views'
import {
  DocumentPage,
  DocumentVersion,
  FlattenedDocumentPage,
} from '@/lib/api/client'
import { useCheckImageDataNeedsUpdate } from '@/utils/hooks/useCheckImageDataNeedsUpdate'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  useFullPartsBOM,
  useFullToolsBOM,
} from '../../OperationDocument/hooks/useBOM'
import { useActivePageFromVirtualizer } from '../../OperationDocument/hooks/useActivePageFromVirtualizer'
import { useRoute } from 'wouter'
import { toChunks } from '@/utils'

export const WorkInstructions = () => {
  const { isLoading, data } = useDocumentPageQuery()
  const isGeneratingPdf = usePDFifyStore((state) => state.isGenerating)
  const documentContainerRef = useRef<HTMLDivElement>(null)
  const firstDocRef = useRef<HTMLDivElement>(null)
  const zoom = useDocumentState((state) => state.zoom)
  const defaultZoom = useDocumentState((state) => state.defaultZoom)
  const setDefaultZoom = useDocumentState((state) => state.setDefaultZoom)
  const registerVirtualizer = useDocumentState(
    (state) => state.registerVirtualizer,
  )
  const setPageOrder = useDocumentState((state) => state.setPageOrder)

  const pages = useMemo(() => data?.documentPages || [], [data?.documentPages])
  const [isPrintPage] = useRoute(/print$/)

  const flattenOperations = useCallback((pages: DocumentPage[]) => {
    const pageMap = new Map<string, DocumentPage>()
    pages.forEach((p) => pageMap.set(p.id as string, p))

    const roots = pages.filter((p) => !p.parent)
    const result: FlattenedDocumentPage[] = []

    const walk = (page: DocumentPage, parentOrder?: string, depth = 0) => {
      const order_number = parentOrder
        ? `${parentOrder}.${page.order_number}`
        : `${page.order_number}`

      result.push({
        ...page,
        order_number,
        depth,
      })

      const children = pages
        .filter((p) => p.parent === page.id)
        .sort((a, b) => a.order_number - b.order_number)
      for (const child of children) {
        walk(child, order_number, depth + 1)
      }
    }

    roots
      .sort((a, b) => a.order_number - b.order_number)
      .forEach((root) => walk(root))

    return result
  }, [])

  const partsBom = useFullPartsBOM({
    onlyCountUsed: true,
  })
  const toolsBom = useFullToolsBOM()

  const flattenedOperations = useMemo(
    () => flattenOperations(pages),
    [pages, flattenOperations],
  )

  const partsBomChunks: Array<BillOfMaterials> = useMemo(() => {
    const partsChunks = toChunks({
      array: partsBom,
      chunkSize: MAX_PARTS_PER_PAGE,
    })

    return partsChunks?.length
      ? partsChunks
      : [
          [
            {
              partName: '',
              quantity: 0,
            },
          ],
        ]
  }, [partsBom])

  const toolsBomChunks: Array<ToolsBillOfMaterials> = useMemo(() => {
    const toolsChunks = toChunks({
      array: toolsBom,
      chunkSize: MAX_PARTS_PER_PAGE,
    })

    return toolsChunks?.length
      ? toolsChunks
      : [
          [
            {
              toolName: '',
            },
          ],
        ]
  }, [toolsBom])

  const versionHistoryChunks: Array<Array<DocumentVersion>> = useMemo(() => {
    const versionNumber = data.documentVersion?.version_number || 1
    const versionIndex =
      data.documentVersions?.findIndex(
        (d) => d.version_number === versionNumber,
      ) ?? 0

    const versions = data.documentVersions?.slice(versionIndex + 1)

    return toChunks({
      array: versions || [],
      chunkSize: MAX_PARTS_PER_PAGE,
    })
  }, [data.documentVersion?.version_number, data.documentVersions])

  const flatPages = useMemo(
    () => [
      { id: 'cover', template_values: {} },
      ...versionHistoryChunks.map((versions, i) => ({
        id: `history-${i}`,
        template_values: {
          subItems: versions,
        },
      })),
      ...partsBomChunks.map((parts, i) => ({
        id: `bom-${i}`,
        template_values: {
          subItems: parts,
        },
      })),
      ...toolsBomChunks.map((tools, i) => ({
        id: `tools_bom-${i}`,
        template_values: {
          subItems: tools,
        },
      })),
      ...flattenedOperations,
    ],
    [partsBom, toolsBom, flattenedOperations, toolsBomChunks, partsBomChunks],
  )

  const operationsSubpages = useMemo(
    () =>
      flattenedOperations.reduce((accum, operation) => {
        const subPages = (operation.template_values as any)?.subPages || []
        return accum + subPages.length
      }, 0),
    [flattenedOperations],
  )

  const baseHeight = (i: number) =>
    Math.max(
      ((flatPages[i]?.template_values as any)?.subPages?.length || 0) * 612,
      612,
    )

  const zoomFactor = zoom / 100
  const pagesVirtualizer = useVirtualizer({
    count: flatPages.length,
    getScrollElement: () => documentContainerRef.current,
    estimateSize: (i) => Math.round(baseHeight(i) * zoomFactor),
    overscan: 2,
    gap: 16,
    enabled: !isPrintPage,
  })

  const pageIds = useMemo(
    () => flatPages.map((p) => p.id as string),
    [flatPages],
  )
  const virtualItems = pagesVirtualizer.getVirtualItems()

  useEffect(() => {
    pagesVirtualizer.measure()
  }, [zoomFactor, pagesVirtualizer, operationsSubpages])

  useActivePageFromVirtualizer({
    scrollEl: documentContainerRef.current,
    virtualizer: pagesVirtualizer,
    virtualItems,
    pageIds,
    zoom: zoomFactor,
    debounceMs: 50,
  })

  useEffect(() => {
    registerVirtualizer(pagesVirtualizer)
  }, [pagesVirtualizer])

  useEffect(() => {
    setPageOrder(pageIds)
  }, [pageIds])

  useEffect(() => {
    setTimeout(() => {
      if (documentContainerRef.current && firstDocRef.current && !defaultZoom) {
        const { height: containerHeight, width: containerWidth } =
          documentContainerRef.current.getBoundingClientRect()

        const { height: firstDocHeight, width: firstDocWidth } =
          firstDocRef.current.getBoundingClientRect()

        const widthRatio = Math.abs(containerWidth / firstDocWidth - 1.05)
        const heightRatio = Math.abs(
          (containerHeight - MAIN_DOCUMENT_CONTAINER_TOP_PADDING) /
            firstDocHeight -
            1.05,
        )

        setDefaultZoom(Math.round(Math.min(widthRatio, heightRatio) * 100))
      }
    })
  }, [
    documentContainerRef,
    firstDocRef,
    data?.project,
    data?.version,
    data?.documentPages,
    defaultZoom,
    setDefaultZoom,
  ])

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (!data || !data.project || !data.version || !data.documentPages) {
    return (
      <div className="h-full overflow-scroll relative pt-14">
        <div className="flex flex-col items-center mt-20">
          <h1 className="text-lg font-semibold">No document found.</h1>
          <h2 className="text-gray-500">
            Capture a smart screenshot to get started.
          </h2>
        </div>
      </div>
    )
  }

  const versionNumber = data.documentVersion?.version_number || 1
  const versionName = data.documentVersion?.name || `V${versionNumber}`
  const createdAt = data.documentVersion?.created || new Date().toISOString()
  const isLatestDocumentVersion = data.isLatestDocumentVersion
  const documentName = data.doc?.name || 'Work Instructions'

  return (
    <DocumentContainer ref={documentContainerRef}>
      <InnerDocumentContainer>
        {isPrintPage ? (
          <>
            <TitleDocument
              documentName={documentName}
              versionName={versionName}
              createdAt={createdAt}
              isLatestDocumentVersion={isLatestDocumentVersion}
            />
            {versionHistoryChunks.map((versions, i) => (
              <VersionHistoryDocument
                key={i}
                versionName={versionName}
                createdAt={createdAt}
                isLatestDocumentVersion={isLatestDocumentVersion}
                versions={versions}
                index={i}
              />
            ))}
            {partsBomChunks.map((parts, i) => (
              <BillOfMaterialsDocument
                key={i}
                versionName={versionName}
                createdAt={createdAt}
                isLatestDocumentVersion={isLatestDocumentVersion}
                parts={parts}
                index={i}
              />
            ))}
            {toolsBomChunks.map((tools, i) => (
              <ToolsBillOfMaterialsDocument
                key={i}
                versionName={versionName}
                createdAt={createdAt}
                isLatestDocumentVersion={isLatestDocumentVersion}
                tools={tools}
                index={i}
              />
            ))}
            {flattenedOperations.map((page) => (
              <OperationStep
                key={page.id}
                page={page}
                cadVersionId={data?.version?.id}
                documentVersionId={data?.documentVersion?.id}
                versionName={versionName}
                createdAt={page.created || createdAt}
                isGeneratingPdf={isGeneratingPdf}
                isLatestDocumentVersion={isLatestDocumentVersion}
              />
            ))}
          </>
        ) : (
          <div
            style={{
              height: pagesVirtualizer.getTotalSize(),
              position: 'relative',
              width: `${792 * zoomFactor}px`,
              margin: '0 auto',
              top: 0,
            }}
          >
            {pagesVirtualizer.getVirtualItems().map((virtualRow, i) => {
              const page = flatPages[virtualRow.index] as any
              const virtualDocumentContainerProps = {
                zoom,
                top: virtualRow.start,
                height: Math.round(baseHeight(virtualRow.index) * zoomFactor),
              }
              if (page.id === 'cover') {
                return (
                  <VirtualDocumentContainer
                    key={virtualRow.key}
                    ref={pagesVirtualizer.measureElement}
                    {...virtualDocumentContainerProps}
                  >
                    <TitleDocument
                      parentRef={i === 0 ? firstDocRef : null}
                      documentName={documentName}
                      versionName={versionName}
                      createdAt={createdAt}
                      isLatestDocumentVersion={isLatestDocumentVersion}
                    />
                  </VirtualDocumentContainer>
                )
              }

              if (/^history-\d+$/.test(page.id)) {
                return (
                  <VirtualDocumentContainer
                    key={virtualRow.key}
                    ref={pagesVirtualizer.measureElement}
                    {...virtualDocumentContainerProps}
                  >
                    <VersionHistoryDocument
                      parentRef={i === 0 ? firstDocRef : null}
                      versionName={versionName}
                      createdAt={createdAt}
                      isLatestDocumentVersion={isLatestDocumentVersion}
                      versions={page.template_values.subItems}
                      index={i}
                    />
                  </VirtualDocumentContainer>
                )
              }

              if (/^bom-\d+$/.test(page.id)) {
                return (
                  <VirtualDocumentContainer
                    key={virtualRow.key}
                    ref={pagesVirtualizer.measureElement}
                    {...virtualDocumentContainerProps}
                  >
                    <BillOfMaterialsDocument
                      parentRef={i === 0 ? firstDocRef : null}
                      versionName={versionName}
                      createdAt={createdAt}
                      isLatestDocumentVersion={isLatestDocumentVersion}
                      parts={page.template_values.subItems}
                      index={i}
                    />
                  </VirtualDocumentContainer>
                )
              }

              if (/^tools_bom-\d+$/.test(page.id)) {
                return (
                  <VirtualDocumentContainer
                    key={virtualRow.key}
                    ref={pagesVirtualizer.measureElement}
                    {...virtualDocumentContainerProps}
                  >
                    <ToolsBillOfMaterialsDocument
                      parentRef={i === 0 ? firstDocRef : null}
                      versionName={versionName}
                      createdAt={createdAt}
                      isLatestDocumentVersion={isLatestDocumentVersion}
                      tools={page.template_values.subItems}
                      index={i}
                    />
                  </VirtualDocumentContainer>
                )
              }

              return (
                <VirtualDocumentContainer
                  key={virtualRow.key}
                  ref={pagesVirtualizer.measureElement}
                  {...virtualDocumentContainerProps}
                >
                  <OperationStep
                    page={page}
                    parentRef={i === 0 ? firstDocRef : null}
                    cadVersionId={data?.version?.id}
                    documentVersionId={data?.documentVersion?.id}
                    versionName={versionName}
                    createdAt={page.created || createdAt}
                    isGeneratingPdf={isGeneratingPdf}
                    isLatestDocumentVersion={isLatestDocumentVersion}
                  />
                </VirtualDocumentContainer>
              )
            })}
          </div>
        )}
      </InnerDocumentContainer>
    </DocumentContainer>
  )
}

type OperationStepProps = {
  page: FlattenedDocumentPage
  cadVersionId?: string | null
  documentVersionId?: string | null
  versionName: string
  createdAt: string
  isGeneratingPdf?: boolean
  isLatestDocumentVersion?: boolean
  parentRef?: any
}
const OperationStep = ({
  page,
  cadVersionId,
  documentVersionId,
  versionName,
  createdAt,
  isGeneratingPdf,
  isLatestDocumentVersion,
  parentRef,
}: OperationStepProps) => {
  const [isPrintPage] = useRoute(/print$/)
  const { isLoading: isLoadingViews, data: viewsData } = useListPageViews({
    cadVersionId,
    documentVersionId,
  })
  const isUserReadOnly = useAppStore((state) => state.isUserReadOnly())

  const views = useMemo(
    () =>
      (viewsData || [])
        .filter((view) => view.document_page === page.id)
        .sort((viewA, viewB) => {
          return (
            (viewA.document_page_order || 0) - (viewB.document_page_order || 0)
          )
        }),
    [viewsData, page.id],
  )

  useCheckImageDataNeedsUpdate({ views })

  if (isLoadingViews) {
    return <div>Loading...</div>
  }

  return (
    <OperationDocument
      parentRef={parentRef}
      key={page.id}
      documentPage={page}
      views={views}
      versionName={versionName}
      orderNumber={page.order_number as any}
      createdAt={page.created || createdAt}
      isReadOnly={
        isGeneratingPdf ||
        !isLatestDocumentVersion ||
        isUserReadOnly ||
        isPrintPage
      }
      isLatestDocumentVersion={isLatestDocumentVersion}
    />
  )
}
